// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Exports viewer-related helper functions that can be expected to be used by multiple extensions.
 */

import type { AutodeskExternalId } from '@evolvemep/api-ts';

import type { TypeSafePropertyResult } from 'components/Autodesk/extensions/propertyHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { THREE } from 'components/Autodesk/viewerThree';
import { isNil } from 'helpers/isNotNil';

import InstanceTree = Autodesk.Viewing.InstanceTree;

/**
 * Wraps getBulkProperties with a promise. Returns an empty array if `dbIds` is empty.
 * @param model the Autodesk model to get the properties from
 * @param dbIds the dbIds of the objects to get properties for
 * @param options options for getting the properties
 *
 * @remarks Never call this function directly; use {@link getBulkPropertiesBatched} instead.
 **/
const getBulkPropertiesAsync = (
  model: Autodesk.Viewing.Model,
  dbIds: Parameters<typeof model.getBulkProperties>[0],
  options: Parameters<typeof model.getBulkProperties>[1],
): Promise<Autodesk.Viewing.PropertyResult[]> =>
  new Promise((resolve, reject) =>
    // `getBulkProperties` will return EVERYTHING if you pass in an empty `dbIds`.
    // This is never preferable, so explicitly check if `dbIds` is empty.
    dbIds.length === 0 ||
    // Since this method is called in async batches, it's possible that the model will be unloaded during the lookup.
    // If that happens, the Property DB will be nil and `getBulkProperties` will throw an (empty) error.
    // Instead, check before the lookup, and return an empty array if there's no Property DB.
    // TODO: This should probably be handled by custom error wrappers
    isNil(model.getPropertyDb())
      ? resolve([])
      : model.getBulkProperties(dbIds, options, resolve, reject),
  );

const BULK_LOOKUP_BATCH_SIZE = 2500;

export type TypeSafeBulkPropertiesOptions<T extends string> = {
  propFilter?: T[] | undefined;
  ignoreHidden?: boolean | undefined;
};

/**
 * Batched version of {@link getBulkPropertiesAsync}.
 *
 * ForgeViewer uses a worker to "post" results back to the caller.
 * This worker cannot handle excessively large responses, so batching the calls circumvents this.
 * */
export const getBulkPropertiesBatched = async <T extends string>(
  model: Autodesk.Viewing.Model,
  dbIds: AutodeskDbId[],
  options?: TypeSafeBulkPropertiesOptions<T>,
): Promise<TypeSafePropertyResult<T>[]> => {
  const results: Autodesk.Viewing.PropertyResult[] = [];
  for (let i = 0; i < dbIds.length; i += BULK_LOOKUP_BATCH_SIZE) {
    results.push(...(await getBulkPropertiesAsync(model, dbIds.slice(i, i + BULK_LOOKUP_BATCH_SIZE), options)));
  }
  return results as TypeSafePropertyResult<T>[];
};

/**
 * Find all objects in `model` whose `propertyToSearch` property contains `searchString`,
 * returning only properties `[propertyToSearch, ...addtlPropertiesToPull]`
 */
export const findAllObjectsWithMatchingProperty = <T extends string>(
  model: Autodesk.Viewing.Model,
  propertyToSearch: T,
  searchString: string,
  addtlPropertiesToPull: T[] = [],
): Promise<TypeSafePropertyResult<T>[]> =>
  new Promise((resolve, reject) => {
    model.search(
      searchString,
      (dbIds: AutodeskDbId[]) =>
        resolve(getBulkPropertiesBatched(model, dbIds, { propFilter: [propertyToSearch, ...addtlPropertiesToPull] })),
      reject,
      [propertyToSearch],
      { searchHidden: true },
    );
  });

/**
 * Find all objects in `model` that contain property `property`,
 * returning only properties `[property, ...addtlPropertiesToPull]`
 */
export const findAllObjectsWithProperty = <T extends string>(
  model: Autodesk.Viewing.Model,
  property: T,
  addtlPropertiesToPull: T[] = [],
): Promise<TypeSafePropertyResult<T>[]> =>
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-call
  model
    // @ts-expect-error This function exists and is async; not sure why it's not on the type
    .findProperty(property)
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-member-access
    .then((dbIds: AutodeskDbId[]) =>
      getBulkPropertiesBatched(model, dbIds, { propFilter: [property, ...addtlPropertiesToPull] }),
    );

/**
 * Wraps getExternalIdMapping with a promise
 * @param model the autodesk model to get the mapping from
 **/
export const getExternalIdMappingAsync = (model: Autodesk.Viewing.Model) =>
  new Promise<Record<AutodeskExternalId, AutodeskDbId>>((res, rej) => {
    model.getExternalIdMapping(res, rej);
  });

/** Wraps {@link Autodesk.Viewing.Model.getObjectTree} with a promise. */
export const getObjectTree = (model: Autodesk.Viewing.Model) =>
  new Promise<InstanceTree>((res, rej) => model.getObjectTree(res, rej));

/**
 * When passed with no arguments, model.getObjectTree synchronously returns the object tree or null if it's not loaded.
 * See {@link https://aps.autodesk.com/en/docs/viewer/v7/reference/Viewing/Model/#getobjecttree-onsuccesscallback-onerrorcallback}
 * It's not typed as such, though. This wrapper adds the correct typing.
 * @param model
 */
export const getObjectTreeSync = (model: Autodesk.Viewing.Model): InstanceTree | null =>
  model.getObjectTree() as unknown as InstanceTree | null;

/**
 * The part of a fragment's geometry `@types/forge-viewer` does not declare. The vertex buffer behind it is
 * deliberately left out: reading it is `VertexEnumerator`'s job, and it knows every layout it arrives in.
 */
export type FragmentGeometry = THREE.Geometry & {
  isLines?: boolean;
};

/** Type alias for a fragment ID. */
export type FragmentId = number;

/**
 * Wrapper around {@link Autodesk.Viewing.InstanceTree.enumNodeFragments}.
 * Returns a list of fragment IDs.
 **/
export const getElementFragmentIds = (
  instanceTree: Autodesk.Viewing.InstanceTree,
  dbId: AutodeskDbId,
  recursive = true,
): FragmentId[] => {
  const result: FragmentId[] = [];
  instanceTree.enumNodeFragments(
    dbId,
    (fragId: FragmentId) => {
      result.push(fragId);
    },
    recursive,
  );
  return result;
};

export type AutodeskFragmentList = Autodesk.Viewing.Private.FragmentList & {
  getWorldMatrix: (fragId: number, matrix: THREE.Matrix4) => void;
  getGeometry?: (fragId: number) => FragmentGeometry | null | undefined;
};

/** Wrapper around model.getFragmentList to return some types omitted by Autodesk. */
export const getFragmentList = (model: Autodesk.Viewing.Model) => model.getFragmentList() as AutodeskFragmentList;

/** Wrapper around `getNodeBox` that returns a `Box3` */
export const getDbIdWorldBounds = (instanceTree: Autodesk.Viewing.InstanceTree, dbId: AutodeskDbId): THREE.Box3 => {
  const rawBounds = new Float32Array(6);
  instanceTree.getNodeBox(dbId, rawBounds);
  const min = new THREE.Vector3(rawBounds[0], rawBounds[1], rawBounds[2]);
  const max = new THREE.Vector3(rawBounds[3], rawBounds[4], rawBounds[5]);
  const boundsBox = new THREE.Box3();
  boundsBox.set(min, max);
  return boundsBox;
};
